import type { OrgTreeNode } from '@/components/OrgTreeChart';
import type { DirectoryUnit } from '@/lib/directory/useDirectory';

/**
 * 조직도 트리의 칸을 만든다 — 직원찾기({@code DirectoryOrgChart})와 인력배치표({@code HqOrgChart})가
 * <b>같은 함수</b>로 세운다. 두 화면이 같은 회사를 다르게 그리면 어느 쪽이 조직도인지 알 수 없다.
 *
 * <h2>부서 목록이 뼈대, 주소록이 살이다</h2>
 * 트리의 층은 부서 관리의 상위 부서(V387)가 정하고, 칸 안의 사람은 주소록 응답에서 <b>이름으로</b>
 * 맞춰 넣는다. 주소록만으로 세우지 않는 이유: 주소록은 사람이 있는 소속만 내려보내서, 「기술부」 처럼
 * 사람 없이 가지만 가진 층이 통째로 빠진다.
 *
 * <p>주소록에는 있는데 부서 목록에 없는 소속(「소속 미지정」)은 뿌리에 따로 세운다 —
 * 이름으로 못 맞췄다고 그 사람들을 떨어뜨리면 찾는 도구가 사람을 숨기는 꼴이다.
 *
 * <p><b>비활성 부서는 세우지 않는다</b>(사용자 지시 2026-10-02 「비활성 했는데 계속 남아있다」).
 * 꺼진 부서에 아직 배치된 사람이 있으면 주소록이 그 이름의 묶음을 내려보내고, 그것이 구석에
 * 계속 섰다. 부서를 껐다는 것은 그 부서를 <b>사람째</b> 치우라는 뜻이다(사용자 확정 2026-10-02) —
 * 명부·전술판도 활성 부서만 세우므로 셋이 같은 답을 한다. 다시 켜면 배치가 그대로라 그대로 돌아온다.
 */
export function departmentNodes(
  departments: Array<{
    id: string;
    name: string;
    parentId: string | null;
    active: boolean;
    chartSlot?: 'TOP' | 'SIDE' | null;
    chartDx?: number;
    chartDy?: number;
  }>,
  units: DirectoryUnit[],
): OrgTreeNode[] {
  const byName = new Map(units.filter((unit) => unit.unitType !== 'SITE').map((unit) => [unit.unitName, unit]));
  const matched = new Set<string>();
  const inactive = new Set(departments.filter((department) => !department.active).map((department) => department.name));

  const nodes: OrgTreeNode[] = departments
    .filter((department) => department.active)
    .map((department) => {
      const unit = byName.get(department.name);
      if (unit) matched.add(department.name);
      return {
        id: department.id,
        name: department.name,
        parentId: department.parentId,
        members: unit ? unit.members : [],
        editable: true,
        slot: department.chartSlot ?? null,
        offset: { dx: department.chartDx ?? 0, dy: department.chartDy ?? 0 },
      };
    });

  // 부서 목록에 없는 소속(소속 미지정)은 오른쪽 구석 — 조직 가운데에 끼우면 층처럼 읽힌다. 꺼진 부서는 뺀다.
  for (const unit of units) {
    if (unit.unitType === 'SITE' || matched.has(unit.unitName) || inactive.has(unit.unitName)) continue;
    nodes.push({
      id: `unit:${unit.unitName}`,
      name: unit.unitName,
      parentId: null,
      members: unit.members,
      editable: false,
      slot: 'SIDE',
    });
  }
  return nodes;
}

/** 현장에는 층이 없다. 「현장」 뿌리 하나 아래 나란히 — 다섯을 넘으면 트리가 두 열로 내린다. */
export function siteNodes(label: string, units: DirectoryUnit[]): OrgTreeNode[] {
  const rootId = 'site-root';
  return [
    { id: rootId, name: label, parentId: null, members: [], editable: false },
    ...units.map((unit) => ({
      id: `site:${unit.unitName}`,
      name: unit.unitName,
      parentId: rootId,
      members: unit.members,
      editable: false,
    })),
  ];
}
